import { isEmpty } from 'lodash'
import moment from 'moment'
import * as yup from 'yup'

import agent from 'agent'
import { ProvenanceTargetType } from 'components/attacks/create/create-attack-types'
import { FontAwesomeIconName, LegacyIconName } from 'components/Icon'
import { isContinuousStatusCheckNode } from 'components/scenarios/utils/graph'
import { trans } from 'i18n/translate'
import toast from 'lib/toast'
import theme from 'theme'
import {
  ScenarioRun,
  ScenarioRunGraph,
  ScenarioRunResults,
  ScenarioRunStageInfo
} from 'types/api/gamedays/scenarios'
import { ReliabilityReport } from 'types/api/policies/reliability-report'
import { ServiceDependency } from 'types/api/services/dependencies/dependency'
import { DependencySuggestionsGroup } from 'types/api/services/dependencies/dependency-suggestions'
import { LoadGenerator } from 'types/api/services/load-generator'
import {
  Process,
  ProcessProvenance,
  UserDefinedService
} from 'types/api/services/service'
import { GraphNode, Lifecycle, Type } from 'types/api/services/status'
import { ScheduleFormSettings } from 'types/forms'
import {
  LoadTestNodeDetails,
  LoadTestNodeStatus,
  LoadTestStatus,
  LoadTestType
} from 'types/load-test'
import { PolicyEvaluation, PolicyResult } from 'types/policy'
import { ScheduleDay, ScheduleSettings } from 'types/schedules/default'
import { WEEK_DAYS } from 'utils/days'
import { formatDate, formatDateTime } from 'utils/formatDate'
import { naturalSortBy, unique } from 'utils/list'
import { capitalize, messageFromResponseError } from 'utils/strings'

export const MAX_SERVICE_NAME_LENGTH = 64
export const MAX_STATUS_CHECK_NAME_LENGTH = 128
export const MAX_DEPENDENCY_NAME_LENGTH = 100

// Defines the scheduling window
export const SCHEDULE_WINDOW_MINIMUM_HOURS = 2
export const SCHEDULE_WINDOW_MAXIMUM_HOURS = 24

// Get full range of possible positions of the end time value relative to start time value in the test schedule window.
// For the default window max and min, this is an Array of values [2, 3, ... , 23, 24]
export const TEST_SCHEDULE_WINDOW_DURATIONS = [
  ...Array(SCHEDULE_WINDOW_MAXIMUM_HOURS - SCHEDULE_WINDOW_MINIMUM_HOURS).keys()
].map((e) => e + SCHEDULE_WINDOW_MINIMUM_HOURS)

export const SERVICE_AUTOSCHEDULE_TRIGGER_TYPES = [
  {
    label: 'All Reliability Tests',
    value: 'Always'
  },
  {
    label: 'Any Reliability Tests that have passed at least once',
    value: 'Passed'
  },
  {
    label: 'Any Reliability Tests that have been run at least once',
    value: 'RunOnce'
  }
]

export enum TestStatus {
  PreparingFor = 'preparing for',
  Running = 'running',
  Passed = 'passed',
  Failed = 'failed',
  Expired = 'expired',
  Attempted = 'attempted'
}

export type HeaderStates = {
  [key in ReliabilityTestStatus]: {
    icon: FontAwesomeIconName | LegacyIconName
    iconColor: string
    testStatus: TestStatus
    testStatusColor: string
    message: (expiryDate?: string) => string
  }
}

// Dictionary object for evaluating and displaying different reliability test states
// TODO: Improve the run status logic to reduce the number of duplicate cases
export const HEADER_STATES: HeaderStates = {
  SETUP: {
    icon: 'solidArrowsRotate',
    iconColor: theme.colors.purple[0],
    testStatus: TestStatus.PreparingFor,
    testStatusColor: theme.colors.purple[0],
    message: () => ''
  },
  ACTIVE: {
    icon: 'solidArrowsRotate',
    iconColor: theme.colors.purple[0],
    testStatus: TestStatus.Running,
    testStatusColor: theme.colors.purple[0],
    message: () => ''
  },
  NOTSTARTED: {
    icon: 'solidArrowsRotate',
    iconColor: theme.colors.purple[0],
    testStatus: TestStatus.Running,
    testStatusColor: theme.colors.purple[0],
    message: () => ''
  },
  PASSED: {
    icon: 'solidCircleCheck',
    iconColor: theme.colors.green[0],
    testStatus: TestStatus.Passed,
    testStatusColor: theme.colors.green[0],
    message: (expiryDate) =>
      trans('service.status_header.reliability_test.results.passed', {
        expiryDate
      })
  },
  SUCCESSFUL: {
    icon: 'solidCircleCheck',
    iconColor: theme.colors.green[0],
    testStatus: TestStatus.Passed,
    testStatusColor: theme.colors.green[0],
    message: (expiryDate) =>
      trans('service.status_header.reliability_test.results.passed', {
        expiryDate
      })
  },
  FAILED: {
    icon: 'solidCircleXmark',
    iconColor: theme.colors.red[0],
    testStatus: TestStatus.Failed,
    testStatusColor: theme.colors.red[0],
    message: () =>
      trans('service.status_header.reliability_test.results.failed')
  },
  EXPIRED: {
    icon: 'solidCircleMinus',
    iconColor: theme.colors.orange[0],
    testStatus: TestStatus.Expired,
    testStatusColor: theme.colors.orange[0],
    message: (expiryDate) =>
      trans('service.status_header.reliability_test.results.expired', {
        expiryDate
      })
  },
  HALTED: {
    icon: 'solidCircleMinus',
    iconColor: theme.colors.steel[1],
    testStatus: TestStatus.Attempted,
    testStatusColor: theme.colors.steel[1],
    message: () =>
      trans('service.status_header.reliability_test.results.halted')
  },
  USERHALTED: {
    icon: 'solidCircleMinus',
    iconColor: theme.colors.steel[1],
    testStatus: TestStatus.Attempted,
    testStatusColor: theme.colors.steel[1],
    message: () =>
      trans('service.status_header.reliability_test.results.user_halted')
  },
  HALTREQUESTED: {
    icon: 'solidCircleMinus',
    iconColor: theme.colors.steel[1],
    testStatus: TestStatus.Attempted,
    testStatusColor: theme.colors.steel[1],
    message: () =>
      trans('service.status_header.reliability_test.results.user_halted')
  },
  UNSURE: {
    icon: 'solidCircleMinus',
    iconColor: theme.colors.steel[1],
    testStatus: TestStatus.Attempted,
    testStatusColor: theme.colors.steel[1],
    message: () => ''
  }
}

export const serviceFormSchemaValidation = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required('Please enter the service name')
    .min(1, 'Service name cannot be empty')
    .matches(/\S+/g, 'Please enter the service name')
    .max(MAX_SERVICE_NAME_LENGTH),
  scheduleSettings: yup.object().shape({
    enabled: yup.boolean().required(),
    triggerType: yup
      .string()
      .oneOf(
        SERVICE_AUTOSCHEDULE_TRIGGER_TYPES.map(
          (triggerType) => triggerType.value
        )
      )
      .when('enabled', {
        is: true,
        then: (schema) => schema.required('Trigger type is required')
      }),
    activeDays: yup.array(yup.mixed<ScheduleDay>()).when('enabled', {
      is: true,
      then: (schema) =>
        schema.required('At least one active day must be selected').min(1)
    }),
    start: yup.date().when('enabled', {
      is: true,
      then: (schema) => schema.required('Start time is required')
    }),
    end: yup
      .date()
      .when('enabled', {
        is: true,
        then: (schema) => schema.required('End time is required')
      })
      .test({
        name: 'isEndTimeInScheduleWindow',
        message: `End time must be between ${SCHEDULE_WINDOW_MINIMUM_HOURS} and ${SCHEDULE_WINDOW_MAXIMUM_HOURS} hours after start time`,
        test: function (value) {
          const diff = moment(value).diff(moment(this.parent.start), 'hours')

          return (
            diff > SCHEDULE_WINDOW_MINIMUM_HOURS ||
            diff < SCHEDULE_WINDOW_MAXIMUM_HOURS
          )
        }
      })
  })
})

export const dependencyFailuresTableValidationSchema = yup.object().shape(
  {
    name: yup
      .string()
      .trim()
      .required('Please enter a dependency name')
      .min(1, 'Dependency name cannot be empty')
      .matches(/\S+/g, 'Please enter the service name')
      .max(MAX_DEPENDENCY_NAME_LENGTH),
    address: yup.string().when('ports', {
      is: (val) => val === undefined || val.trim().length === 0,
      then: yup
        .string()
        .trim()
        .required('An address is required when a port is not entered'),
      otherwise: yup.string()
    }),
    ports: yup.string().when('address', {
      is: (val) => val === undefined || val.trim().length === 0,
      then: yup
        .string()
        .trim()
        .required('A port is required when an address is not entered'),
      otherwise: yup.string()
    })
  },
  [['address', 'ports']]
)

export type Dependency = {
  address?: string
  name: string
  ports?: string
  id?: string
}

export function getProvenanceByTargetType(
  targetType: ProvenanceTargetType
): ProcessProvenance {
  switch (targetType) {
    case 'Host':
      return ProcessProvenance.ProcessData
    case 'Container':
      return ProcessProvenance.Container
    case 'Kubernetes':
      return ProcessProvenance.Kubernetes
  }
}

export function getUniqueServiceProcesses(processes: Process[]): Process[] {
  return unique(Object.entries(processes).map(([, v]) => v))
}

export function filterReliabilityScore(score: number | string) {
  // to ensure that we only display the score digits exactly as we want to in the UI
  // convert the number to a float and round it to 2 decimal places
  // then pass it back through Number() to force unnecessary trailing zeroes to be removed
  return Number(Number.parseFloat(String(score)).toFixed(2))
}

export function getStatusCheckNodesFromBranches(
  scenarioRunGraphs
): GraphNode[] {
  if (!scenarioRunGraphs || scenarioRunGraphs.length === 0) return []

  const graphNodes = scenarioRunGraphs
    .flatMap((s) => s.nodes)
    .flatMap((n) => Object.values(n)) as GraphNode[]

  const statusChecks = graphNodes
    // Filter the graph nodes for Status Checks
    .filter((node) =>
      (node.type === Type.ContinuousStatusCheck ||
        node.type === Type.SynchronousStatusCheck) &&
      node.isHaltable === undefined
        ? true
        : node.isHaltable
    )
    // Ensure that each Status Check returned from this function is
    // a) unique
    // b) the last run for that particular signal
    .reduce((existingNodes: GraphNode[], thisNode) => {
      if (!existingNodes.some((n) => n.id === thisNode.id)) {
        existingNodes.push(thisNode)
      } else {
        existingNodes.forEach((existingNode, index) => {
          const isStartedLater =
            new Date(existingNode.state.start_time).getTime() <
            new Date(thisNode.state.start_time).getTime()

          if (existingNode.id === thisNode.id && isStartedLater) {
            existingNodes.splice(index, 1, thisNode)
          }
        })
      }

      return existingNodes
    }, [] as GraphNode[])

  const sortedStatusChecks = naturalSortBy(statusChecks, (n) => n.name, 'asc')

  return sortedStatusChecks
}

const findNodeInGraph = (
  webhookId: string,
  branches: ScenarioRunGraph[]
): GraphNode =>
  branches
    .flatMap((b) => Object.values(b.nodes))
    .find((n) => n.id === webhookId)

export function getLoadGeneratorNodesFromBranches(
  branches: ScenarioRunGraph[] | undefined,
  loadGenerator: LoadGenerator[]
): {
  id: string
  name: string
  triggerNode: GraphNode
  stopNode: GraphNode
  loadStatusNode: GraphNode
}[] {
  if (!branches?.length || !loadGenerator?.length) {
    return []
  }

  return loadGenerator.map((l) => ({
    name: l.name,
    id: l.identifier,
    triggerNode: findNodeInGraph(l.webhook_start_load_trigger_id, branches),
    loadStatusNode: findNodeInGraph(l.webhook_status_check_id, branches),
    stopNode: findNodeInGraph(l.webhook_stop_load_trigger_id, branches)
  }))
}

export const computeLoadGeneratorNodesStatus = (
  loadGenerators: LoadTestNodeDetails[]
): LoadTestNodeStatus[] =>
  loadGenerators.map((l) => {
    const triggerStatus = computeLoadTestNodeStatus(
      l.triggerNode,
      LoadTestType.Start
    )
    const checkStatus = computeLoadTestNodeStatus(
      l.loadStatusNode,
      LoadTestType.Status
    )
    const stopStatus = computeLoadTestNodeStatus(l.stopNode, LoadTestType.Stop)

    const errorAtStart = triggerStatus === LoadTestStatus.Error
    const errorAtStatusCheck = checkStatus === LoadTestStatus.Error
    const errorAtStop = stopStatus === LoadTestStatus.Error

    const errorAt =
      (errorAtStart && LoadTestType.Start) ||
      (errorAtStatusCheck && LoadTestType.Status) ||
      (errorAtStop && LoadTestType.Stop) ||
      null

    return {
      loadGeneratorId: l.id,
      loadGeneratorName: l.name,
      status:
        stopStatus || checkStatus || triggerStatus || LoadTestStatus.NotStarted,
      errorAt: errorAt
    }
  })

const passedStatuses: Record<LoadTestType, LoadTestStatus> = {
  Start: LoadTestStatus.Triggered,
  Stop: LoadTestStatus.Stopped,
  Status: LoadTestStatus.Running
}

export const computeLoadTestNodeStatus = (
  node: GraphNode,
  type: LoadTestType
): LoadTestStatus | undefined => {
  const isHalted = node?.state?.lifecycle === Lifecycle.Halted
  if (isHalted) {
    return LoadTestStatus.Halted
  }

  const passed = node?.state?.evaluation_results?.okStatusCodes?.passed
  if (passed === true) {
    return passedStatuses[type]
  }
  if (passed === false) {
    return LoadTestStatus.Error
  }
}

export type PolicyEvaluationStatus = PolicyResult | 'NEVER_RUN'

export function getPolicyEvaluationStatus(
  policyEvaluation: PolicyEvaluation
): PolicyEvaluationStatus {
  if (!policyEvaluation?.evaluationTime) {
    return 'NEVER_RUN'
  }

  return policyEvaluation?.result
}

// TODO: Refactor this function to accomodate values from the PolicyEvaluation type
export function getReliabilityTestRunResultColor(resultState = '') {
  switch (resultState.toLowerCase()) {
    case 'passed':
      return 'success'
    case 'failed':
      return 'error'
    case 'unsure':
      return 'error'
    case 'expired':
      return 'warning'
    default:
      return 'default'
  }
}

export function getPrettyRunStatus(status) {
  return status
    ?.split('_')
    ?.map((s) => capitalize(s))
    ?.join(' ')
}

export function getIconRunStatus(status: PolicyResult) {
  switch (status) {
    case 'PASSED':
      return 'solidCheck'
    case 'FAILED':
      return 'solidClose'
    case 'EXPIRED':
      return 'solidWarning'
    case 'NEVER_RUN':
    default:
      return 'solidMinus'
  }
}

export function isCriticalDependency(dependency: ServiceDependency) {
  // TODO implement this as part of https://gremlininc.atlassian.net/browse/EN-2365
  return false && dependency // just ensuring that dependency is used to avoid lint issue
}

export function getScheduleSettings(scheduleSettings) {
  const {
    activeDays,
    start,
    end,
    enabled,
    numOfRuns,
    scheduleType,
    triggerType
  } = scheduleSettings

  const triggers =
    activeDays.length > 0
      ? activeDays.map((activeDay) => ({
          dayOfWeek: activeDay,
          scheduleWindows: [
            {
              start: moment.utc(start).format('HH:mm'), // 24 hour UTC time
              end: moment.utc(end).format('HH:mm'), // 24 hour UTC time
              numOfRuns: numOfRuns || 1 // Defaulting to 1 for Impala V1
            }
          ]
        }))
      : null

  return {
    enabled,
    scheduleType: scheduleType || 'Random', // Defaulting to 'Random' for Impala V1
    triggerType,
    triggers
  }
}

export function getInitialScheduleFormSettings(
  scheduleSettings: ScheduleSettings = { enabled: false }
): ScheduleFormSettings {
  const { enabled, triggers, triggerType } = scheduleSettings

  // Default active day to Monday to line up with default start time
  const activeDays =
    triggers?.length > 0
      ? triggers.map((value) => {
          return value.dayOfWeek
        })
      : [ScheduleDay.Monday]

  const autoScheduleStart = moment().utc().hour(12).minute(0).day(1) // Initialize autoScheduling start time to 12pm UTC time with day set to Monday
  const autoScheduleEnd = moment(autoScheduleStart).add(2, 'hours') // Initialize autoScheduling end time to 2 hours past start time

  //generates initial form values
  const start =
    triggers?.length > 0
      ? moment
          .utc(triggers[0].scheduleWindows[0].start, 'HH:mm')
          .day(WEEK_DAYS.indexOf(triggers[0].dayOfWeek))
      : autoScheduleStart

  const end =
    triggers?.length > 0
      ? moment
          .utc(triggers[0].scheduleWindows[0].end, 'HH:mm')
          .day(WEEK_DAYS.indexOf(triggers[0].dayOfWeek))
      : autoScheduleEnd

  if (start.isAfter(end)) {
    end.add(1, 'day')
  }

  return {
    enabled,
    triggerType: triggerType || 'Passed',
    activeDays,
    start: start.format(),
    end: end.format()
  }
}

export function getScheduleWindowText(schedule: ScheduleSettings) {
  if (schedule?.triggers?.length < 1) {
    return ''
  }

  const days = {
    Su: 'Sun',
    M: 'Mon',
    T: 'Tue',
    W: 'Wed',
    Th: 'Thu',
    F: 'Fri',
    S: 'Sat'
  }

  const scheduledDailyTimeWindows = schedule?.triggers?.map((trigger) => {
    const timeWindows = trigger.scheduleWindows?.map((window) => {
      return `${moment.utc(window.start, 'HH:mm').format('ha')} - ${moment
        .utc(window.end, 'HH:mm')
        .format('ha')}`
    })
    return `${days[trigger.dayOfWeek]}: ${timeWindows}`
  })

  return scheduledDailyTimeWindows?.join('\n')
}

export function getServiceScheduleText(scheduleSettings: ScheduleSettings) {
  const dailyWindowsText = getScheduleWindowText(scheduleSettings)
  if (!dailyWindowsText) {
    return ''
  }
  return `This test will be queued to potentially run during these times:\n\n${dailyWindowsText} (UTC)`
}

export function isAutoSchedulingValid(values) {
  const { activeDays, enabled, start, end } = values

  const startMoment = start ? moment(start) : null
  const endMoment = end ? moment(end) : null

  if (enabled) {
    // Check that both times have been entered
    if (!start || !end) {
      return true
    }

    // Check that the end time is within the defined time window for the scheduler
    if (
      endMoment.diff(startMoment, 'hours') < SCHEDULE_WINDOW_MINIMUM_HOURS ||
      endMoment.diff(startMoment, 'hours') > SCHEDULE_WINDOW_MAXIMUM_HOURS
    ) {
      return true
    }

    // Check that any days have been selected
    if (activeDays?.length < 1) {
      return true
    }
  }

  return false
}

export function isReliabilityTestExpired(expiry: number): boolean {
  const e_date = new Date(expiry)
  const now = new Date(Date.now())
  return now.getTime() - e_date.getTime() >= 0
}

export type ReliabilityTestStatus =
  | Uppercase<ScenarioRunResults['status']>
  | Uppercase<Lifecycle>
  | 'SETUP'
  | 'EXPIRED'

export function getReliabilityTestStatus(
  policyState: PolicyEvaluation,
  latestRun: ScenarioRun
): ReliabilityTestStatus {
  const stageInfo =
    latestRun?.stageInfo ??
    latestRun?.stage_info ??
    ({} as ScenarioRunStageInfo)
  const latestRunStage = stageInfo?.stage
  const latestRunResultsStatus = latestRun?.results?.status

  if (latestRunStage === 'Setup') {
    return 'SETUP'
  }

  // If the run is still active, nothing else matters
  if (latestRunStage === 'Active') {
    return 'ACTIVE'
  }

  // Evaluate the run results object
  if (latestRunResultsStatus === 'Passed') {
    if (
      isReliabilityTestExpired(
        policyState.evaluationTime + policyState.staleness
      )
    ) {
      return 'EXPIRED'
    } else {
      return latestRunResultsStatus.toUpperCase() as ReliabilityTestStatus
    }
  } else if (latestRunResultsStatus === 'Failed') {
    return latestRunResultsStatus.toUpperCase() as ReliabilityTestStatus
  }

  const latestNode = latestRun?.graph.nodes_recursive.find(
    (node) =>
      node?.state.endTime === undefined && !isContinuousStatusCheckNode(node)
  )

  let latestNodeStatus = latestNode?.state.lifecycle.toUpperCase()
  if (
    latestNodeStatus !== 'USERHALTED' &&
    latestRunResultsStatus === 'Unsure' // This indicates the test has been halted
  ) {
    latestNodeStatus = 'HALTED'
  }
  return latestNodeStatus as ReliabilityTestStatus
}

export function hasTestBeenRun(policyEvaluation: PolicyEvaluation) {
  const runStatus = getPolicyEvaluationStatus(policyEvaluation)
  return runStatus !== 'NEVER_RUN'
}

export function isTestScheduled(
  service: UserDefinedService,
  reliabilityTestId: string,
  dependencyId?: string
) {
  if (dependencyId) {
    return (
      service?.schedulableTests?.find(
        (t) =>
          t.reliabilityTestId === reliabilityTestId &&
          t.dependencyId === dependencyId
      ) !== undefined
    )
  } else {
    return (
      service?.schedulableTests?.find(
        (t) => t.reliabilityTestId === reliabilityTestId && !t.dependencyId
      ) !== undefined
    )
  }
}

export async function runReliabilityTest(
  policyEvaluation: PolicyEvaluation,
  currentTeamId: string,
  onSuccess = () => null,
  dependencyId = ''
) {
  try {
    const params: { serviceId: string; dependencyId?: string } = {
      serviceId: policyEvaluation.serviceId
    }

    if (dependencyId) {
      params.dependencyId = dependencyId
    }

    await agent.post(
      `/reliability-tests/${policyEvaluation.reliabilityTestId}/runs?teamId=${currentTeamId}`,
      params
    )

    toast({
      type: 'success',
      text: `Running ${policyEvaluation.name} Test`
    })

    onSuccess()
  } catch (e) {
    toast({
      type: 'error',
      text: messageFromResponseError(
        e,
        `There was an issue running this ${policyEvaluation.name} Test ${
          dependencyId && ' on this Dependency'
        }. Please try again later.`
      )
    })
  }
}

export async function runAllTests(
  serviceId: string,
  currentTeamId: string,
  onSuccess = () => null
) {
  try {
    await agent.post(
      `/services/${serviceId}/baseline?teamId=${currentTeamId}`,
      {
        startBaselineRequest: {
          tests: []
        }
      }
    )

    toast({
      type: 'success',
      text: 'Running All Tests'
    })

    onSuccess()
  } catch (e) {
    toast({
      type: 'error',
      text: messageFromResponseError(
        e,
        'There was an issue running all tests on this service. Please try again later.'
      )
    })
  }
}

export const DONUT_RELIABILITY_COLOR_MAP = {
  SCALABILITY: '#AA00FF',
  REDUNDANCY: '#651FFF',
  DEPENDENCIES: '#40C4FF'
}

export function getReliabilityScoreData(reliabilityReport: ReliabilityReport) {
  return reliabilityReport?.reliability
    ? naturalSortBy(
        Object.entries(reliabilityReport.reliability),
        (c) => c[1].rank
      ).map(([key, value]) => ({
        gremlin: key,
        count: value.score
      }))
    : []
}

export function getTestResultFromRunStatus(
  status: ScenarioRunResults['status']
) {
  switch (status) {
    case 'Passed':
      return 'Pass'
    case 'Failed':
      return 'Fail'
    case 'Unsure':
    default:
      return 'Attempted'
  }
}

export function getJiraIssueSummary(
  service: UserDefinedService,
  run: ScenarioRun,
  reliabilityId: string
) {
  return [
    'Gremlin',
    `${service.name}`,
    reliabilityId,
    `${run && formatDate(run.created_at)}`
  ].join('-')
}

export function getJiraIssueDescription(
  service: UserDefinedService,
  run: ScenarioRun,
  reliabilityId: string
) {
  return [
    `*Service Name:*\n${service.name}`,
    `*Reliability Test Run:*\n${getReliabilityTestRunLink(
      service.serviceId,
      run?.run_number,
      reliabilityId
    )}`,
    `*Last Run:*\n${run && formatDateTime(run.created_at)}`,
    `*Result:*\n${run?.results?.status}`
  ].join('\n\n')
}

function getReliabilityTestRunLink(serviceId, runNumber, reliabilityTestId) {
  return `${window.location.origin}/services/${serviceId}/status/${reliabilityTestId}/run/${runNumber}`
}

/**
 * Function will sanitize the policy name passed in to remove redundant 'Test' strings from the view
 * @param name
 */
export function getDisplayPolicyName(name: string): string {
  if (!name) {
    return ''
  }

  if (String(name).match(/Test$/)) {
    return name
  }

  return `${name} Test`
}

export const isTargetParamsEmpty = (targetParams) =>
  isEmpty(targetParams) ||
  Object.values(targetParams).every((paramValues) => {
    if (paramValues instanceof Set) {
      return paramValues.size === 0
    } else {
      return !paramValues
    }
  })

export function getPolicyStateForTest(
  reliabilityReport: ReliabilityReport,
  reliabilityTestId: string,
  dependencyId?: string
) {
  const policyStatesForTest = Object.entries(
    reliabilityReport?.reliability ?? {}
  )
    .flatMap(([, category]) => category.policyStates)
    .filter((s) => s.reliabilityTestId === reliabilityTestId)

  if (dependencyId) {
    return policyStatesForTest.find((s) => s.dependencyId === dependencyId)
  }

  // TODO pretty sure there should never be more than one, but we may want to validate this and note it here
  return policyStatesForTest[0]
}

export function convertCentsToDollars(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  })
}

export function isProductionService(service: UserDefinedService) {
  return service?.tags?.environment === 'production'
}

export function isServiceSetupComplete(service: UserDefinedService) {
  return service?.statusCheckIds?.length > 0
}

export function hasServiceRunATest(reliabilityReport: ReliabilityReport) {
  if (!reliabilityReport?.reliability) {
    return false
  }

  let hasRun = false
  Object.entries(reliabilityReport.reliability).forEach(([, category]) => {
    category?.policyStates?.forEach((policyState) => {
      if (hasTestBeenRun(policyState)) {
        hasRun = true
      }
    })
  })

  return hasRun
}

export type GroupByOption = {
  value: DependencySuggestionsGroup
  label: string
}

export type GroupByOptions = {
  [key in DependencySuggestionsGroup]: GroupByOption
}

export const DEPENDENCY_SUGGESTIONS_GROUP_BY_OPTIONS: GroupByOptions = {
  [DependencySuggestionsGroup.PORT]: {
    value: DependencySuggestionsGroup.PORT,
    label: 'Port'
  },
  [DependencySuggestionsGroup.IP]: {
    value: DependencySuggestionsGroup.IP,
    label: 'IP Address'
  }
}
export const formatOwnerDetails = (identifier, firstName, lastName) => {
  return firstName && lastName
    ? `${firstName} ${lastName} (${identifier})`
    : firstName
    ? `${firstName} (${identifier})`
    : lastName
    ? `${lastName} (${identifier})`
    : identifier
}
